import { useEffect, useRef, useState, type ReactNode } from "react";
import { en as t } from "../i18n/en";
import { colorHex } from "../lib/colors";
import { sfx, soundPref } from "../lib/sound";
import { themePref, toggleTheme } from "../lib/theme";
import type { PlayerView } from "../protocol";

const TIMER_FRAME_MS = 100;
const TICK_FROM_SECONDS = 10;

export function Avatar({ player, size = 36 }: { player: Pick<PlayerView, "name" | "color">; size?: number }) {
  return (
    <span
      className="avatar"
      aria-hidden="true"
      style={{ background: colorHex(player.color), width: size, height: size, fontSize: size * 0.45 }}
    >
      {player.name.slice(0, 1).toUpperCase()}
    </span>
  );
}

/** Milliseconds since a server timestamp, ticking with the synced clock. */
export function useElapsed(since: number | undefined, serverNow: () => number, until: number): number {
  const [elapsed, setElapsed] = useState(() => (since ? serverNow() - since : until));
  useEffect(() => {
    if (!since) return setElapsed(until);
    const update = () => {
      const e = serverNow() - since;
      setElapsed(e);
      return e < until;
    };
    if (!update()) return;
    const id = window.setInterval(() => {
      if (!update()) window.clearInterval(id);
    }, TIMER_FRAME_MS);
    return () => window.clearInterval(id);
  }, [since, serverNow, until]);
  return elapsed;
}

/**
 * A countdown bar that drains smoothly to the server deadline (synced clock),
 * ticking each second for the last ten. The bar is animated directly on the
 * element every frame, so React only re-renders when the number changes.
 */
export function Timer({ deadline, serverNow, total }: { deadline?: number; serverNow: () => number; total: number }) {
  const bar = useRef<HTMLDivElement>(null);
  const [secs, setSecs] = useState(() => (deadline ? Math.ceil(Math.max(0, deadline - serverNow()) / 1000) : 0));
  const lastSecs = useRef(secs);

  useEffect(() => {
    if (!deadline) return;
    const totalMs = total * 1000;
    let raf = 0;
    const frame = () => {
      const left = Math.max(0, deadline - serverNow());
      if (bar.current) bar.current.style.transform = `scaleX(${totalMs > 0 ? Math.min(1, left / totalMs) : 0})`;
      // Voting deadlines include the option reveal; never count above the limit.
      const s = Math.min(total, Math.ceil(left / 1000));
      if (s !== lastSecs.current) {
        if (s > 0 && s <= TICK_FROM_SECONDS && s < lastSecs.current) sfx.tick();
        lastSecs.current = s;
        setSecs(s);
      }
      if (left > 0) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [deadline, serverNow, total]);

  if (!deadline) return null;
  const urgent = secs <= TICK_FROM_SECONDS;
  // Announce only the milestones, not every second (TDD: Accessibility).
  const announce = secs === 30 || secs === 10 ? `${secs} seconds left` : "";
  return (
    <div className={`timer${urgent ? " urgent" : ""}`} role="timer">
      <div className="timer-bar" ref={bar} />
      <span className="timer-num" aria-hidden="true">{secs}</span>
      <span className="sr-only" aria-live="polite">{announce}</span>
    </div>
  );
}

export function Toast({ message, onDone }: { message: string; onDone: () => void }) {
  useEffect(() => {
    const id = window.setTimeout(onDone, 3500);
    return () => window.clearTimeout(id);
  }, [message, onDone]);
  return (
    <div className="toast" role="status">
      {message}
    </div>
  );
}

export function Sheet({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <h2 className="sheet-title">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function SoundIcon({ on }: { on: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 5 6 9H3v6h3l5 4z" fill="currentColor" />
      {on ? <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" /> : <path d="m16 9 6 6m0-6-6 6" />}
    </svg>
  );
}

function ThemeIcon({ dark }: { dark: boolean }) {
  return dark ? (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="currentColor">
      <path d="M21 13.5A8.5 8.5 0 1 1 10.5 3a7 7 0 0 0 10.5 10.5z" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="4.5" fill="currentColor" />
      <path d="M12 2v2m0 16v2M2 12h2m16 0h2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

/** The bar on every screen: wordmark, optional middle content, sound and theme toggles. */
export function TopBar({ center, extra }: { center?: ReactNode; extra?: ReactNode }) {
  const sound = soundPref.use();
  const theme = themePref.use();
  return (
    <header className="topbar">
      <span className="topbar-brand">Desi<span>Psych</span></span>
      <span className="topbar-center">{center}</span>
      <span className="topbar-actions">
        {extra}
        <button
          type="button"
          className="icon-btn"
          aria-label={sound === "on" ? t.soundOff : t.soundOn}
          aria-pressed={sound === "on"}
          onClick={() => soundPref.set(sound === "on" ? "off" : "on")}
        >
          <SoundIcon on={sound === "on"} />
        </button>
        <button
          type="button"
          className="icon-btn"
          aria-label={theme === "dark" ? t.lightTheme : t.darkTheme}
          onClick={toggleTheme}
        >
          <ThemeIcon dark={theme === "dark"} />
        </button>
      </span>
    </header>
  );
}
